import { Calendar, ChevronDown, Download, RotateCcw, Search, SearchX } from "lucide-react";
import Button from "@/components/ui/Button";
import DataTable, { type Column } from "@/components/ui/DataTable";
import EmptyState from "@/components/ui/EmptyState";
import Select from "@/components/ui/Select";
import { TableSkeleton } from "@/components/ui/Skeleton";
import type { WalletTxn } from "@/types/wallet";
import {
  formatDate,
  formatINR,
  formatTime,
  isPositiveTxn,
  RANGES,
  TXN_TYPE_META,
  TYPE_OPTIONS,
} from "@/pages/retailer/wallet/data";


interface TransactionsTabProps {
  loading: boolean;
  search: string;
  onSearch: (v: string) => void;
  retailer: string;
  onRetailer: (v: string) => void;
  type: string;
  onType: (v: string) => void;
  range: string;
  onRange: (v: string) => void;
  rangeOpen: boolean;
  onRangeOpen: (v: boolean) => void;
  onReset: () => void;
  rows: WalletTxn[];
  retailerOptions: { value: string; label: string }[];
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (p: number) => void;
  onPageSizeChange: (n: number) => void;
  openMandate: (refNo: string) => void;
  showRetailerFilter?: boolean;
  showRetailerColumn?: boolean;
  showCreatedBy?: boolean;
  showExport?: boolean;
  onExport?: () => void;
}

export default function TransactionsTab(props: TransactionsTabProps) {
  const {
    loading,
    search,
    onSearch,
    retailer,
    onRetailer,
    type,
    onType,
    range,
    onRange,
    rangeOpen,
    onRangeOpen,
    onReset,
    rows,
    retailerOptions,
    page,
    pageSize,
    total,
    onPageChange,
    onPageSizeChange,
    openMandate,
    showRetailerFilter = false,
    showRetailerColumn = false,
    showCreatedBy = false,
    showExport = false,
    onExport,
  } = props;

  const columns: Column<WalletTxn>[] = [
    {
      key: "createdAt",
      header: "Date",
      render: (row) => (
        <div>
          <p className="text-[13px] text-ink">{formatDate(row.createdAt)}</p>
          <p className="text-xs text-ink-secondary">{formatTime(row.createdAt)}</p>
        </div>
      ),
    },
    {
      key: "txnId",
      header: "Transaction ID",
      render: (row) => <span className="font-mono text-[13px] text-ink-secondary">{row.txnId}</span>,
    },
  ];

  if (showRetailerColumn) {
    columns.push({
      key: "retailerId",
      header: "Retailer",
      render: (row) => {

        return (
          <div>
            <p className="text-sm font-medium text-ink">{row.shopName || row.retailerName || "Retailer"}</p>
            <p className="text-xs text-ink-secondary">{row.retailerName && row.retailerName !== row.shopName ? row.retailerName : (!row.retailerId?.includes("-") ? row.retailerId : "")}</p>
          </div>
        );
      },
    });
  }

  columns.push(
    {
      key: "description",
      header: "Description",
      render: (row) => (
        <div>
          <p className="text-sm font-medium text-ink">{row.category}</p>
          <p className="text-xs text-ink-secondary">
            {row.description}
            {row.refNo && (
              <>
                {" · "}
                <button
                  type="button"
                  onClick={() => row.refNo && openMandate(row.refNo)}
                  className="font-medium text-primary-500 hover:text-primary-600 cursor-pointer"
                >
                  {row.refNo}
                </button>
              </>
            )}
          </p>
        </div>
      ),
    },
    {
      key: "type",
      header: "Type",
      render: (row) => {
        const meta = TXN_TYPE_META[row.type];
        return (
          <span className={`inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium ${meta.bg} ${meta.text}`}>
            {meta.label}
          </span>
        );
      },
    },
    {
      key: "amount",
      header: "Amount",
      align: "right",
      render: (row) => {
        const positive = isPositiveTxn(row.type) || (row.type === "ADJUSTMENT" && row.amount > 0);
        const sign = positive ? "+" : "-";
        const value = positive ? row.amount : Math.abs(row.amount);
        return (
          <span className={`whitespace-nowrap text-sm font-semibold ${positive ? "text-success" : "text-danger"}`}>
            {sign}
            {formatINR(value)}
          </span>
        );
      },
    },
    {
      key: "balanceAfter",
      header: "Balance After",
      align: "right",
      render: (row) => <span className="whitespace-nowrap text-sm text-ink-secondary">{formatINR(row.balanceAfter)}</span>,
    }
  );

  if (showCreatedBy) {
    columns.push({
      key: "createdBy",
      header: "Created By",
      render: (row) => <span className="text-[13px] text-ink-secondary">{row.createdBy}</span>,
    });
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-3 rounded-[10px] border border-line bg-card p-4 shadow-card lg:flex-row lg:items-center">
        <div className="flex flex-1 flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 flex h-5 w-5 items-center justify-center text-ink-muted">
              <Search className="h-4 w-4" />
            </span>
            <input
              type="text"
              value={search}
              onChange={(e) => onSearch(e.target.value)}
              placeholder={
                showRetailerFilter
                  ? "Search by transaction ID, retailer or reference number"
                  : "Search by transaction ID, reference number or description"
              }
              className="h-[42px] w-full rounded-lg border border-slate-300 bg-white pl-10 pr-3.5 text-sm text-ink placeholder:text-ink-muted focus:outline-none focus:ring-2 focus:border-primary-500 focus:ring-primary-500/30"
            />
          </div>
          {showRetailerFilter && (
            <div className="sm:w-52">
              <Select value={retailer} onChange={(e) => onRetailer(e.target.value)} options={[{ value: "", label: "All Retailers" }, ...retailerOptions]} />
            </div>
          )}
          <div className="sm:w-44">
            <Select value={type} onChange={(e) => onType(e.target.value)} options={TYPE_OPTIONS} />
          </div>
          <div className="relative sm:w-44">
            <button
              type="button"
              onClick={() => onRangeOpen(!rangeOpen)}
              className="flex h-[42px] w-full items-center justify-between rounded-lg border border-slate-300 bg-white px-3.5 text-sm text-ink hover:bg-slate-50 cursor-pointer"
            >
              <span className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-ink-muted" />
                {range}
              </span>
              <ChevronDown className="h-4 w-4 text-ink-muted" />
            </button>
            {rangeOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => onRangeOpen(false)} />
                <div className="absolute right-0 z-20 mt-2 w-44 rounded-lg border border-line bg-white py-1 shadow-dropdown animate-fadeIn">
                  {RANGES.map((r) => (
                    <button
                      key={r}
                      type="button"
                      onClick={() => {
                        onRange(r);
                        onRangeOpen(false);
                      }}
                      className={`block w-full whitespace-nowrap px-4 py-2 text-left text-sm cursor-pointer ${
                        range === r ? "bg-primary-50 text-primary-600" : "text-ink hover:bg-slate-50"
                      }`}
                    >
                      {r}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {showExport && (
            <Button variant="secondary" onClick={onExport} leftIcon={<Download className="h-4 w-4" />}>
              Export
            </Button>
          )}
          <Button variant="ghost" onClick={onReset} leftIcon={<RotateCcw className="h-4 w-4" />}>
            Reset
          </Button>
        </div>
      </div>
      {loading ? (
        <TableSkeleton rows={5} columns={columns.length} />
      ) : rows.length === 0 ? (
        <EmptyState icon={SearchX} title="No transactions found" description="Try adjusting your search, type or date range." actionLabel="Reset Filters" onAction={onReset} />
      ) : (
        <DataTable
          columns={columns}
          data={rows}
          rowKey={(r) => r.id}
          pageSizeOptions={[25, 50, 100]}
          serverPagination={{ page, pageSize, total, onPageChange, onPageSizeChange }}
        />
      )}
    </div>
  );
}